/**
 * SplitTransactionDialog - Navigate to split a transaction into multiple documents
 * 
 * Single Responsibility: Present split options and navigate to document creation
 * Interface: Receives transaction data and navigation handler
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Scissors, FileText, Receipt, Wallet, ArrowUpRight, ArrowDownRight } from 'lucide-react';

export const SplitTransactionDialog = ({
  open,
  onOpenChange,
  transaction,
  onNavigateToSplit,
  formatAmount
}) => {
  const handleClose = () => {
    onOpenChange(false);
  };

  const handleNavigate = (documentType) => {
    if (transaction) {
      onNavigateToSplit(transaction, documentType);
      handleClose();
    }
  };

  if (!transaction) return null;

  const isInflow = transaction.direction === 'inflow';
  const DirectionIcon = isInflow ? ArrowDownRight : ArrowUpRight;
  const directionColor = isInflow ? 'text-green-600' : 'text-red-600';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-[#108981]" />
            Split Transaction
          </DialogTitle>
          <DialogDescription>
            Split this transaction into multiple accounting entries
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Transaction Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <DirectionIcon className={`w-5 h-5 ${directionColor}`} />
              <span className={`font-semibold ${directionColor}`}>
                {isInflow ? '+' : '-'}{formatAmount(transaction.amount, transaction.currency || transaction.bank_currency)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{transaction.description}</p>
            <p className="text-xs text-muted-foreground">{transaction.date}</p>
          </div>

          {/* Split Options */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Create split entries as:</p>
            <div className="grid grid-cols-2 gap-3">
              {isInflow ? (
                <>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300"
                    onClick={() => handleNavigate('invoice')}
                    data-testid="split-as-invoice"
                  >
                    <Receipt className="w-6 h-6 text-green-600" />
                    <span className="font-medium">Multiple Invoices</span>
                    <span className="text-xs text-muted-foreground text-center">
                      Split across different customer payments
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-300"
                    onClick={() => handleNavigate('other_receipt')}
                    data-testid="split-as-receipt"
                  >
                    <Wallet className="w-6 h-6 text-blue-600" />
                    <span className="font-medium">Multiple Receipts</span>
                    <span className="text-xs text-muted-foreground text-center">
                      Split across different income types
                    </span>
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-orange-50 hover:border-orange-300"
                    onClick={() => handleNavigate('bill')}
                    data-testid="split-as-bill"
                  >
                    <FileText className="w-6 h-6 text-orange-600" />
                    <span className="font-medium">Multiple Bills</span>
                    <span className="text-xs text-muted-foreground text-center">
                      Split across different vendor payments
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-purple-50 hover:border-purple-300"
                    onClick={() => handleNavigate('other_payment')}
                    data-testid="split-as-payment"
                  >
                    <Wallet className="w-6 h-6 text-purple-600" />
                    <span className="font-medium">Multiple Payments</span>
                    <span className="text-xs text-muted-foreground text-center">
                      Split across different expense types
                    </span>
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              <strong>Tip:</strong> You'll be taken to the document creation page where you can allocate 
              portions of this transaction to different accounts or contacts.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SplitTransactionDialog;
